import { useBatchLabelPrint } from '@/pages/Business/pages/Shippings/hooks/useBatchLabelPrint';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useCloneCancelledRoute } from './useCloneCancelledRoute';
import useNotification from '@/hooks/useNotification';
import { useCancelRoute } from './useCancelRoute';
import { useNavigate } from 'react-router-dom';
import type { Route } from '../models/Route';
import { useState } from 'react';
import {
  isRouteCancellable,
  isRouteCancelled,
  isRouteDriverEditable,
  isRouteReadOnly,
  isUnassignedPlaceholderDriver,
} from '../utils/routeInteractionRules';

export interface UseRouteCardActionsParams {
  route: Route;
}

export function useRouteCardActions({ route }: UseRouteCardActionsParams) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { routeBuilderBasePath, allowCancelRoute = true } =
    useMonitoringIntegration();

  const [isExpanded, setIsExpanded] = useState(false);
  const [assignDriverModalOpen, setAssignDriverModalOpen] = useState(false);
  const [addShippingsModalOpen, setAddShippingsModalOpen] = useState(false);
  const [cancelRouteModalOpen, setCancelRouteModalOpen] = useState(false);

  const hasDriver = Boolean(route.driver) && !isUnassignedPlaceholderDriver(route.driver);
  const isCancelled = isRouteCancelled(route.status);
  const isReadOnly = isRouteReadOnly(route.status);

  const canAddShippings = Boolean(route.logisticOperatorId) && !isReadOnly;

  const canEditRoute = Boolean(route.logisticOperatorId) && !isReadOnly;

  const canAssignDriver =
    Boolean(route.logisticOperatorId) &&
    !isReadOnly &&
    (!hasDriver || isRouteDriverEditable(route));

  const driverActionLabel = hasDriver
    ? 'Reasignar conductor'
    : 'Asignar conductor';

  const driverActionDisabledReason = !route.logisticOperatorId
    ? 'La ruta no tiene operador logístico.'
    : hasDriver
      ? 'La ruta ya comenzó su recorrido.'
      : 'No se puede asignar conductor a una ruta completada, cancelada o cerrada.';

  const canCancelRoute = allowCancelRoute && isRouteCancellable(route);

  const canCloneRoute = isCancelled && Boolean(route.logisticOperatorId);

  const canPrintLabels =
    !isCancelled &&
    Boolean(route.logisticOperatorId) &&
    (route.ordersCount ?? 0) > 0;

  const printLabelsDisabledReason = !route.logisticOperatorId
    ? 'La ruta no tiene operador logístico.'
    : 'La ruta no tiene pedidos.';

  const {
    printRoute,
    isPrinting: isPrintingLabels,
    job: labelsJob,
    dismissJob: dismissLabelsJob,
  } = useBatchLabelPrint();

  const { cancelRoute, isCancelling } = useCancelRoute({
    routeId: route.id,
    logisticOperatorId: route.logisticOperatorId,
    onSuccess: () => setCancelRouteModalOpen(false),
  });

  const { cloneRoute, isCloning } = useCloneCancelledRoute({
    routeId: route.id,
    logisticOperatorId: route.logisticOperatorId,
  });

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const closeAssignDriverModal = () => setAssignDriverModalOpen(false);
  const closeAddShippingsModal = () => setAddShippingsModalOpen(false);
  const closeCancelRouteModal = () => {
    if (!isCancelling) setCancelRouteModalOpen(false);
  };

  const handleEditRoute = () => {
    if (!canEditRoute) {
      showNotification(
        'No se puede editar una ruta completada, cancelada o cerrada.',
        NotificationSeverity.warning
      );
      return;
    }
    const params = new URLSearchParams({
      editRouteId: route.id,
      logisticOperatorId: route.logisticOperatorId,
    });
    navigate(`${routeBuilderBasePath}?${params.toString()}`);
  };

  const handleAssignDriverClick = () => {
    if (!canAssignDriver) {
      showNotification(
        driverActionDisabledReason,
        NotificationSeverity.warning
      );
      return;
    }
    setAssignDriverModalOpen(true);
  };

  const handleAddShippingsClick = () => {
    if (!canAddShippings) {
      showNotification(
        'No se pueden agregar pedidos a una ruta completada o cancelada.',
        NotificationSeverity.warning
      );
      return;
    }
    setAddShippingsModalOpen(true);
  };

  const handleCancelRouteClick = () => {
    if (isCancelled) {
      showNotification(
        'La ruta ya está cancelada.',
        NotificationSeverity.warning
      );
      return;
    }
    if (!canCancelRoute) {
      showNotification(
        'Solo se pueden cancelar rutas que todavía no salieron a recorrer.',
        NotificationSeverity.warning
      );
      return;
    }
    setCancelRouteModalOpen(true);
  };

  const handleConfirmCancelRoute = () => {
    void cancelRoute();
  };

  const handleCloneRouteClick = () => {
    if (!canCloneRoute) {
      showNotification(
        'Solo se pueden clonar rutas canceladas.',
        NotificationSeverity.warning
      );
      return;
    }
    void cloneRoute();
  };

  const handlePrintLabelsClick = () => {
    if (!canPrintLabels) {
      showNotification(printLabelsDisabledReason, NotificationSeverity.warning);
      return;
    }
    void printRoute(
      { routeId: route.id, logisticOperatorId: route.logisticOperatorId },
      route.ordersCount
    );
  };

  return {
    isExpanded,
    toggleExpanded,
    assignDriverModalOpen,
    closeAssignDriverModal,
    addShippingsModalOpen,
    closeAddShippingsModal,
    cancelRouteModalOpen,
    closeCancelRouteModal,
    hasDriver,
    isCancelled,
    isReadOnly,
    canAddShippings,
    canEditRoute,
    canAssignDriver,
    driverActionLabel,
    driverActionDisabledReason,
    allowCancelRoute,
    canCancelRoute,
    canCloneRoute,
    isCancelling,
    isCloning,
    handleEditRoute,
    handleAssignDriverClick,
    handleAddShippingsClick,
    handleCancelRouteClick,
    handleConfirmCancelRoute,
    handleCloneRouteClick,
    canPrintLabels,
    printLabelsDisabledReason,
    isPrintingLabels,
    labelsJob,
    dismissLabelsJob,
    handlePrintLabelsClick,
  };
}
